/***************************************************************************************
 * FileName      : projects.ts
 * Description   : ketri-portal 카드 화면에 노출할 프로젝트 목록.
 *                 - 새 프로젝트 추가 = 이 배열에 항목 한 줄 추가.
 *                 - subpath: ketri-portal 내부 경로 (예: '/scs-home-app') — 클릭 시 이 경로로 진입.
 *                 - devUrl/prodUrl: iframe 으로 임베드할 실제 앱 URL.
 *                     · localhost 접속 → devUrl
 *                     · 그 외(EC2 등) → prodUrl (없으면 devUrl 폴백)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.04.27
 * Modified Date : 2026.04.28 subpath/devUrl/prodUrl 로 구조 변경 (서브패스 라우팅 + iframe 임베드)
 ***************************************************************************************/

export interface ProjectEntry {
  /** 고유 ID (라우팅·로깅용) */
  id: string;
  /** ketri-portal 내부 경로 (예: '/scs-home-app') — 카드 클릭 시 이 경로로 진입 */
  subpath: string;
  /** 카드 제목 */
  title: string;
  /** 카드 부제 (1줄) */
  subtitle?: string;
  /** 카드 본문 설명 (1~2문장) */
  description: string;
  /** 로컬 개발용 앱 URL (iframe src) */
  devUrl: string;
  /** 운영 환경용 앱 URL (iframe src) — 없으면 devUrl 폴백 */
  prodUrl?: string;
  /** 카드 좌측 색상 띠 (브랜드/구분 용) */
  accentColor?: string;
  /** 우상단 뱃지 텍스트 (예: 'beta', '운영') */
  badge?: string;
}

// 2026.04.27 : 박은정 : 초기 노출 프로젝트 목록 (scs-home-app, ketri-smart_ict_lab-app)
//   추후 프로젝트 추가 시 이 배열에 한 항목씩만 늘리면 됨.
export const PROJECTS: ProjectEntry[] = [
  {
    id: 'scs-home-app',
    subpath: '/scs-home-app',
    title: 'SCS Home 공간',
    subtitle: '주거 공간 IoT 디지털트윈',
    description: '집을 3D 스캔하고 IoT 센서 데이터 등을 결합한 데모.',
    // 2026-04-29 : 로컬 dev-server 매핑 수정 — scs-home-app 실제 포트는 8002 (이전엔 8003 으로 잘못 적혀 있어 ketri-portal 자신이 임베드되거나 scs↔ketri 가 뒤바뀌는 문제 있었음).
    devUrl: 'http://localhost:8002/',
    // 운영(예: dt.ketri.re.kr)에선 같은 도메인의 서브패스로 nginx 가 프록시. 상대경로라 도메인 종속성 없음.
    prodUrl: '/scs-home-app/',
    accentColor: '#10B981',
  },
  {
    id: 'ketri-smart_ict_lab-app',
    subpath: '/ketri-smart_ict_lab-app',
    title: '여수산학융합원 공간',
    subtitle: '화학공정 실습 교육',
    description: '여수산학융합원을 3D 스캔하고 모니터·라벨·시나리오 기반 학습 화면을 결합한 데모.',
    // 2026-04-29 : 로컬 dev-server 매핑 수정 — ketri-smart_ict_lab-app 실제 포트는 8001 (이전엔 8002 였는데 그건 scs-home-app 자리).
    devUrl: 'http://localhost:8001/',
    prodUrl: '/ketri-smart_ict_lab-app/',
    accentColor: '#3B82F6',
  },
  {
    // 2026-07-02 : 박은정 : 순천제일대 캠퍼스 전경 (운영 nginx 서브패스 /map/ → localhost:8003)
    id: 'scjeil-campus-app',
    subpath: '/scjeil-campus-app',
    title: '순천제일대학교 캠퍼스',
    subtitle: '캠퍼스 전경 투어',
    description: '순천제일대학교 전경을 3D 스캔하고 주요 건물을 페이드로 둘러보는 캠퍼스 투어 데모.',
    devUrl: 'http://localhost:8003/',
    prodUrl: '/map/',
    accentColor: '#1CA0DB',
  },
];

/**
 * 현재 환경(localhost / 기타)에 따라 iframe src 로 사용할 URL 을 결정.
 */
export const resolveAppUrl = (project: ProjectEntry): string => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  if (isLocal) return project.devUrl;
  return project.prodUrl || project.devUrl;
};
